import React, { useState } from 'react';
import './accordionTable.sass';

/**
 * Componente de accordion para mostrar múltiples stages de tablas de posiciones
 * Permite expandir/colapsar secciones con solo una abierta a la vez
 * El primer item está abierto por defecto para mejor UX
 * 
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.stages - Array de stages/divisiones del torneo
 * @param {string} props.stages[].id - ID único del stage
 * @param {string} props.stages[].name - Nombre del stage/división
 * @param {Function} props.renderTable - Función que renderiza la tabla para cada stage
 * @param {string} [props.variant="primary"] - Variante de estilo ("primary" para nivel superior, "nested" para nivel interior)
 * 
 * @returns {JSX.Element} Accordion con secciones expandibles
 */
export default function AccordionTable({ stages, renderTable, variant = "primary" }) {
  
  // Ordenar stages alfabéticamente por nombre
  const sortedStages = [...stages].sort((a, b) => {
    const nameA = a.name || '';
    const nameB = b.name || '';
    return nameA.localeCompare(nameB);
  });
  
  /**
   * Estado que controla qué sección del accordion está abierta
   * Inicializado en 0 para que el primer item esté abierto por defecto
   */
  const [openAccordion, setOpenAccordion] = useState(0);

  /**
   * Maneja el toggle de las secciones del accordion
   * Solo permite una sección abierta a la vez
   * 
   * @param {number} index - Índice de la sección a togglear
   */
  const handleAccordionToggle = (index) => {
    setOpenAccordion(prev => prev === index ? null : index);
  };

  return (
    <div className={`accordion-component accordion-component--${variant}`}>
      {sortedStages.map((tab, index) => (
        <div key={tab.id || tab.name || `accordion-${index}`} className="accordion-item">
          
          {/* Header del accordion - Botón clickeable */}
          <button
            className={`accordion-header accordion-header--${variant} ${openAccordion === index ? 'active' : ''}`}
            onClick={() => handleAccordionToggle(index)}
            aria-expanded={openAccordion === index}
            aria-controls={`accordion-content-${index}`}
            type="button"
          >
            <span className="accordion-title">{tab.name}</span>
            
            {/* Icono indicador de estado */}
            <span 
              className="accordion-icon"
              aria-hidden="true"
            >
              {openAccordion === index ? '−' : '+'}
            </span>
          </button>

          {/* Contenido del accordion - Se muestra solo si está abierto */}
          {openAccordion === index && (
            <div 
              className="accordion-content"
              id={`accordion-content-${index}`}
              role="region"
              aria-labelledby={`accordion-header-${index}`}
            >
              {renderTable(tab)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}